import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Empty,
  Input,
  InputNumber,
  Modal,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd'
import { ThunderboltOutlined } from '@ant-design/icons'
import ShopHint from './ShopHint'
import { getApiErrorMessage } from '../../utils/api'
import {
  generateVariants,
  getProductVariants,
  updateProductVariants
} from '../../api/shop'

const { Text } = Typography

/**
 * 商品規格（SKU）編輯
 *
 * 規格庫存與商品層庫存是互斥的：
 *   開啟規格後庫存記在各規格上，商品層的總庫存變成自動加總的顯示值；
 *   關閉規格則把各規格庫存合併回商品層。
 * 這件事對店家不直覺，所以介面上要一直提醒，關閉時也要明講庫存會怎麼合併。
 */
export default function ProductVariantModal ({ open, product, onClose, onSaved }) {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [hasVariants, setHasVariants] = useState(false)
  const [rows, setRows] = useState([])
  const [info, setInfo] = useState(null)

  const productId = product?._id || product?.id

  const load = useCallback(async () => {
    if (!productId) return
    setLoading(true)
    try {
      const res = await getProductVariants(productId)
      const data = res.data.data
      setInfo(data)
      setHasVariants(data.hasVariants)
      setRows(
        data.variants.map((v) => ({
          key: v._id || v.optionKey,
          _id: v._id,
          optionKey: v.optionKey,
          options: v.options,
          specText: v.specText,
          sku: v.sku,
          // priceOverridden=false 代表繼承商品層的價格。
          // 這裡刻意留空而不是填入繼承值，店家才看得出「這格我沒設過」。
          price: v.priceOverridden ? v.price : undefined,
          cost: v.costOverridden ? v.cost : undefined,
          stock: v.stock,
          status: v.status
        }))
      )
    } catch (e) {
      message.error(getApiErrorMessage(e, '載入規格失敗'))
    } finally {
      setLoading(false)
    }
  }, [productId])

  useEffect(() => {
    if (open) load()
  }, [open, load])

  /** 依商品的規格軸自動生成所有組合，已存在的會帶回現值 */
  const handleGenerate = async () => {
    setLoading(true)
    try {
      const res = await generateVariants(productId)
      const data = res.data.data
      setRows(
        data.rows.map((r) => ({
          key: r.optionKey,
          optionKey: r.optionKey,
          options: r.options,
          specText: r.specText,
          sku: r.sku,
          price: r.price,
          cost: r.cost,
          stock: r.stock,
          status: r.status,
          isNew: r.isNew
        }))
      )
      setHasVariants(true)
      message.success(`已產生 ${data.count} 種規格組合`)
    } catch (e) {
      message.error(getApiErrorMessage(e, '產生規格組合失敗'))
    } finally {
      setLoading(false)
    }
  }

  const patchRow = (key, patch) => {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)))
  }

  /**
   * 批次填入。規格組合有十幾種時，一列一列填價格與庫存非常痛苦，
   * 而且很容易漏填某一列導致存檔被擋。
   */
  const fillAll = (field, label) => {
    let value = null
    Modal.confirm({
      title: `批次填入${label}`,
      content: (
        <InputNumber
          min={0}
          style={{ width: '100%', marginTop: 8 }}
          placeholder={`所有規格的${label}都會設為這個值`}
          onChange={(v) => {
            value = v
          }}
        />
      ),
      okText: '填入',
      cancelText: '取消',
      onOk: () => {
        if (value === null || value === undefined) {
          message.error(`請輸入${label}`)
          return Promise.reject(new Error('empty'))
        }
        setRows((rs) => rs.map((r) => ({ ...r, [field]: value })))
      }
    })
  }

  const handleSave = async () => {
    if (hasVariants && !rows.length) {
      message.error('請先產生規格組合')
      return
    }
    setSaving(true)
    try {
      const payload = hasVariants
        ? {
            hasVariants: true,
            variants: rows.map((r, i) => ({
              _id: r._id,
              optionKey: r.optionKey,
              options: r.options,
              specText: r.specText,
              sku: r.sku,
              price: r.price,
              cost: r.cost,
              stock: r.stock,
              status: r.status,
              sortOrder: i
            }))
          }
        : { hasVariants: false }

      const res = await updateProductVariants(productId, payload)
      message.success(res.data.message || '規格已更新')
      onSaved?.()
      onClose?.()
    } catch (e) {
      // 後端會把所有驗證問題放在 data.errors，逐條顯示比只顯示第一條有用
      const errors = e?.response?.data?.data?.errors
      if (Array.isArray(errors) && errors.length > 1) {
        message.error({
          content: (
            <div style={{ textAlign: 'left' }}>
              {errors.map((m, i) => (
                <div key={i}>• {m}</div>
              ))}
            </div>
          ),
          duration: 6
        })
      } else {
        message.error(getApiErrorMessage(e, '儲存失敗'))
      }
    } finally {
      setSaving(false)
    }
  }

  const totalStock = rows.reduce((s, r) => s + (Number(r.stock) || 0), 0)

  const columns = [
    {
      title: '規格',
      dataIndex: 'specText',
      width: 160,
      fixed: 'left',
      render: (v, r) => (
        <Space direction='vertical' size={0}>
          <Text strong style={{ fontSize: 13 }}>
            {v}
          </Text>
          {r.isNew && <Tag color='blue'>新增</Tag>}
        </Space>
      )
    },
    {
      title: '料號 SKU',
      dataIndex: 'sku',
      width: 130,
      render: (v, r) => (
        <Input
          size='small'
          value={v}
          placeholder='選填'
          onChange={(e) => patchRow(r.key, { sku: e.target.value })}
        />
      )
    },
    {
      title: (
        <Space size={4}>
          售價
          <Tooltip title='留空則使用商品的售價'>
            <Button
              type='link'
              size='small'
              style={{ padding: 0 }}
              onClick={() => fillAll('price', '售價')}
            >
              批次
            </Button>
          </Tooltip>
        </Space>
      ),
      dataIndex: 'price',
      width: 120,
      render: (v, r) => (
        <InputNumber
          size='small'
          min={0}
          value={v}
          style={{ width: '100%' }}
          placeholder={
            info?.defaults?.price != null ? `${info.defaults.price}` : ''
          }
          onChange={(val) => patchRow(r.key, { price: val })}
        />
      )
    },
    {
      title: (
        <Space size={4}>
          成本
          <Button
            type='link'
            size='small'
            style={{ padding: 0 }}
            onClick={() => fillAll('cost', '成本')}
          >
            批次
          </Button>
        </Space>
      ),
      dataIndex: 'cost',
      width: 120,
      render: (v, r) => (
        <InputNumber
          size='small'
          min={0}
          value={v}
          style={{ width: '100%' }}
          placeholder={info?.defaults?.cost != null ? `${info.defaults.cost}` : ''}
          onChange={(val) => patchRow(r.key, { cost: val })}
        />
      )
    },
    {
      title: (
        <Space size={4}>
          庫存
          <Button
            type='link'
            size='small'
            style={{ padding: 0 }}
            onClick={() => fillAll('stock', '庫存')}
          >
            批次
          </Button>
        </Space>
      ),
      dataIndex: 'stock',
      width: 110,
      render: (v, r) => (
        <InputNumber
          size='small'
          min={0}
          value={v}
          style={{ width: '100%' }}
          onChange={(val) => patchRow(r.key, { stock: val })}
        />
      )
    },
    {
      title: '販售中',
      dataIndex: 'status',
      width: 80,
      render: (v, r) => (
        <Switch
          size='small'
          checked={v === 'active'}
          onChange={(c) => patchRow(r.key, { status: c ? 'active' : 'inactive' })}
        />
      )
    }
  ]

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={900}
      title={`規格設定 — ${product?.name || product?.title || ''}`}
      okText='儲存'
      cancelText='取消'
      confirmLoading={saving}
      onOk={handleSave}
    >
      <Space direction='vertical' size={12} style={{ width: '100%' }}>
        <ShopHint tone='info' title='開啟規格後，庫存會記在各規格上'>
          商品的總庫存會自動加總各規格的數量。進貨、盤點、結帳時都要指定規格。
        </ShopHint>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            flexWrap: 'wrap'
          }}
        >
          <Space>
            <Text strong>使用規格庫存</Text>
            {hasVariants
              ? (
                <Switch
                  checked
                  onChange={() => {
                    Modal.confirm({
                      title: '關閉規格庫存？',
                      content: `各規格的庫存會合併回商品層（合計 ${totalStock}），現有規格設定會被清除。`,
                      okText: '確定關閉',
                      cancelText: '取消',
                      onOk: () => {
                        setHasVariants(false)
                        setRows([])
                      }
                    })
                  }}
                />
                )
              : (
                <Switch checked={false} onChange={() => setHasVariants(true)} />
                )}
          </Space>

          {hasVariants && (
            <Space>
              <Text type='secondary' style={{ fontSize: 12 }}>
                目前總庫存 {totalStock}
              </Text>
              <Button
                icon={<ThunderboltOutlined />}
                onClick={handleGenerate}
                loading={loading}
              >
                依規格軸自動產生組合
              </Button>
            </Space>
          )}
        </div>

        {hasVariants && info?.specifications?.length > 0 && (
          <Space wrap size={4}>
            <Text type='secondary' style={{ fontSize: 12 }}>
              規格軸：
            </Text>
            {info.specifications.map((s, i) => (
              <Tag key={i}>
                {s.name}：{(s.options || []).join('、')}
              </Tag>
            ))}
          </Space>
        )}

        {hasVariants && info?.orphanVariants?.length > 0 && (
          <ShopHint
            tone='warning'
            title={`有 ${info.orphanVariants.length} 組規格已不在目前的規格軸中`}
          >
            這些規格會被停售但保留，以免歷史訂單查不到規格資訊。
          </ShopHint>
        )}

        {!hasVariants && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description='此商品目前使用單一庫存。開啟上方開關以設定不同規格的價格與庫存。'
          />
        )}

        {hasVariants && rows.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description='尚未建立規格組合。請先在商品編輯頁設定規格軸（例如「顏色：白、粉」），再點上方按鈕自動產生。'
          />
        )}

        {hasVariants && rows.length > 0 && (
          <Table
            size='small'
            rowKey='key'
            loading={loading}
            columns={columns}
            dataSource={rows}
            pagination={false}
            scroll={{ x: 720, y: 400 }}
          />
        )}
      </Space>
    </Modal>
  )
}
